import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  X, 
  Briefcase, 
  UserCheck, 
  Package, 
  Users,
  Clock,
  Calendar,
  ArrowRight
} from 'lucide-react';
import { useProjects } from '../contexts/ProjectsContext';
import { useClients } from '../contexts/ClientsContext';
import { useInventory } from '../contexts/InventoryContext';
import { useTeam } from '../contexts/TeamContext';
import { formatCurrency } from '../utils/formatUtils';
import { useFocusTrap, useKeyboardNavigation } from '../hooks/useKeyboardNavigation';

interface SearchResult {
  id: string;
  type: 'project' | 'client' | 'inventory' | 'team';
  title: string;
  subtitle: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  metadata?: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { clients } = useClients();
  const { inventory } = useInventory();
  const { teamMembers } = useTeam();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [resultElements, setResultElements] = useState<HTMLElement[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const modalRef = useFocusTrap(isOpen);

  // Keyboard navigation for search results
  useKeyboardNavigation(resultElements, {
    onSelect: (index) => {
      if (results[index]) {
        results[index].action();
      }
    },
    onEscape: onClose,
    disabled: !isOpen
  });
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Update result elements when results change
  useEffect(() => {
    if (resultsRef.current) {
      const elements = Array.from(
        resultsRef.current.querySelectorAll('[data-result-item]')
      ) as HTMLElement[];
      setResultElements(elements);
    }
  }, [results]);
  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    const searchResults: SearchResult[] = [];
    const term = searchTerm.toLowerCase();

    // Search Projects
    projects.forEach(project => {
      if (
        project.title.toLowerCase().includes(term) ||
        project.description.toLowerCase().includes(term) ||
        project.client.name.toLowerCase().includes(term) ||
        project.notes?.toLowerCase().includes(term)
      ) {
        searchResults.push({
          id: project.id,
          type: 'project',
          title: project.title,
          subtitle: `Client: ${project.client.name}`,
          description: project.description,
          icon: <Briefcase className="w-5 h-5 text-blue-500" />,
          metadata: `${project.status} • ${project.priority} priority`,
          action: () => {
            navigate(`/projects/${project.id}`);
            onClose();
          }
        });
      }
    });

    // Search Clients
    clients.forEach(client => {
      if (
        client.name.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.company?.toLowerCase().includes(term) ||
        client.address.toLowerCase().includes(term) ||
        client.notes?.toLowerCase().includes(term)
      ) {
        const clientProjects = projects.filter(p => 
          p.client.name.toLowerCase() === client.name.toLowerCase()
        );
        
        searchResults.push({
          id: client.id,
          type: 'client',
          title: client.name,
          subtitle: client.company || client.address,
          description: client.email || client.phone,
          icon: <UserCheck className="w-5 h-5 text-green-500" />,
          metadata: `${clientProjects.length} project${clientProjects.length !== 1 ? 's' : ''}`,
          action: () => {
            navigate(`/projects?client=${encodeURIComponent(client.name)}`);
            onClose();
          }
        });
      }
    });

    // Search Inventory
    inventory.forEach(item => {
      if (
        item.name.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term)
      ) {
        const isLowStock = item.minimumStock !== undefined && item.stock <= item.minimumStock;
        
        searchResults.push({
          id: item.id,
          type: 'inventory',
          title: item.name,
          subtitle: `${item.category} • ${formatCurrency(item.unitPrice)}`,
          description: item.description,
          icon: <Package className="w-5 h-5 text-purple-500" />,
          metadata: `${item.stock} ${item.unit}${isLowStock ? ' (Low Stock)' : ''}`,
          action: () => {
            navigate('/inventory');
            onClose();
          }
        });
      }
    });

    // Search Team Members
    teamMembers.forEach(member => {
      if (
        member.name.toLowerCase().includes(term) ||
        member.email.toLowerCase().includes(term) ||
        member.skills.some(skill => skill.toLowerCase().includes(term)) ||
        member.role.toLowerCase().includes(term)
      ) {
        searchResults.push({
          id: member.id,
          type: 'team',
          title: member.name,
          subtitle: `${member.role} • ${formatCurrency(member.hourlyRate)}/hr`,
          description: member.email,
          icon: <Users className="w-5 h-5 text-orange-500" />,
          metadata: member.isActive ? 'Active' : 'Inactive',
          action: () => {
            navigate('/team');
            onClose();
          }
        });
      }
    });

    // Sort results by relevance (exact matches first, then partial matches)
    searchResults.sort((a, b) => {
      const aExact = a.title.toLowerCase().startsWith(term) ? 1 : 0;
      const bExact = b.title.toLowerCase().startsWith(term) ? 1 : 0;
      if (aExact !== bExact) return bExact - aExact;
      
      // Then by type priority (projects first, then clients, inventory, team)
      const typePriority = { project: 4, client: 3, inventory: 2, team: 1 };
      return typePriority[b.type] - typePriority[a.type];
    });

    setResults(searchResults.slice(0, 10)); // Limit to 10 results
    setSelectedIndex(0);
  }, [searchTerm, projects, clients, inventory, teamMembers, navigate, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          results[selectedIndex].action();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  const handleResultClick = (result: SearchResult, index: number) => {
    setSelectedIndex(index);
    result.action();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-16 md:pt-20 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-title"
        ref={modalRef}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-3 md:mx-4 max-h-[80vh] md:max-h-96 overflow-hidden">
        {/* Search Input */}
        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="search-title" className="sr-only">Global Search</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              className="w-full pl-10 pr-10 py-2 md:py-3 bg-gray-100 dark:bg-gray-700 rounded-lg 
                        text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 
                        focus:ring-blue-500 dark:focus:ring-blue-400 text-base md:text-lg"
              role="searchbox"
              aria-label="Search projects, clients, inventory, and team members"
              aria-describedby="search-instructions"
            />
            <button
              onClick={onClose}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            <div id="search-instructions" className="sr-only">
              Use arrow keys to navigate results, Enter to select, Escape to close.
              {results.length > 0 && ` ${results.length} results found.`}
            </div>
          </div>
        </div>

        {/* Search Results */}
        <div 
          ref={resultsRef} 
          className="max-h-[calc(80vh-4rem)] md:max-h-80 overflow-y-auto"
          role="listbox"
          aria-label="Search results"
        >
          {searchTerm.trim() && results.length === 0 && (
            <div className="p-6 md:p-8 text-center text-gray-500 dark:text-gray-400">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No results found for "{searchTerm}"</p>
              <p className="text-xs md:text-sm mt-2">Try searching for projects, clients, inventory items, or team members</p>
            </div>
          )}

          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              data-result-item
              onClick={() => handleResultClick(result, index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleResultClick(result, index);
                }
              }}
              className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                         ${index === selectedIndex 
                           ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700' 
                           : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                         }`}
              role="option"
              aria-selected={index === selectedIndex}
              tabIndex={index === selectedIndex ? 0 : -1}
            >
              <div className="flex items-start space-x-2 md:space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {result.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {result.title}
                    </h3>
                    <div className="flex items-center ml-1 md:ml-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        result.type === 'project' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                        result.type === 'client' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' :
                        result.type === 'inventory' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200' :
                        'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200'
                      }`}>
                        {result.type === 'project' ? 'Project' :
                         result.type === 'client' ? 'Client' :
                         result.type === 'inventory' ? 'Inventory' :
                         'Team'}
                      </span>
                      <ArrowRight className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 truncate mt-0.5 md:mt-1">
                    {result.subtitle}
                  </p>
                  {result.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 truncate mt-0.5 md:mt-1">
                      {result.description}
                    </p>
                  )}
                  {result.metadata && (
                    <p className="text-xs text-gray-400 dark:text-gray-600 mt-0.5 md:mt-1">
                      {result.metadata}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div className="p-2 md:p-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
            <div className="flex flex-col xs:flex-row items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2 md:space-x-4 mb-1 xs:mb-0">
                <span className="flex items-center">
                  <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-xs">↑↓</kbd>
                  <span className="ml-1">Navigate</span>
                </span>
                <span className="flex items-center">
                  <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-xs">Enter</kbd>
                  <span className="ml-1">Select</span>
                </span>
                <span className="flex items-center">
                  <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-xs">Esc</kbd>
                  <span className="ml-1">Close</span>
                </span>
              </div>
              <span className="text-xs">{results.length} result{results.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;